import { inject, Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import {
    HttpInterceptor,
    HttpHandler,
    HttpRequest,
    HttpEvent,
    HttpErrorResponse,
    HttpResponse,
} from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AppStore } from '../store/app.store';

const TOKEN_HEADER_KEY = 'Authorization';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private isRefreshing = false;
    private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
    private authService = inject(AuthService)
    private router = inject(Router);
    private appStore = inject(AppStore);

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const token = this.authService.getAccessToken();
        const authReq = token ? this.addTokenHeader(req, token) : req;

        return next.handle(authReq).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.status === 401 && !authReq.url.includes('FPCAuthenticate')) {
                    return this.handle401Error(authReq, next);
                }
                return throwError(() => error);
            }),
        );
    }

    private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshTokenSubject.next(null);

            const tokenData = this.authService.getTokenData();
            if (!tokenData || !tokenData.refreshToken || !tokenData.accessToken) {
                this.logoutAndRedirect();
                return of(new HttpResponse({ status: 401, statusText: 'Unauthorized' }));
            }

            return this.authService.refreshToken(tokenData).pipe(
                switchMap((token: any) => {
                    this.isRefreshing = false;
                    this.refreshTokenSubject.next(token.accessToken);
                    return next.handle(this.addTokenHeader(request, token.accessToken));
                }),
                catchError((err: HttpErrorResponse) => {
                    if (err.status === 401) {
                        this.logoutAndRedirect();
                        return of(new HttpResponse({ status: 401, statusText: 'Unauthorized' }));
                    }

                    this.isRefreshing = false;
                    return throwError(() => err);
                }),
            );
        }
        return this.refreshTokenSubject.pipe(
            filter((token) => token !== null),
            take(1),
            switchMap((token) => next.handle(this.addTokenHeader(request, token!))),
        );
    }

    private logoutAndRedirect() {
        this.appStore.resetUserData();
        this.authService.logout();
        this.router.navigate(['/login']);
    }

    private addTokenHeader(request: HttpRequest<any>, token: string) {
        return request.clone({ headers: request.headers.set(TOKEN_HEADER_KEY, 'Bearer ' + token) });
    }
}
